/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { FormControl } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import TextWidget from './TextWidget';
import AssistiveText from './AssistiveText';
import { TooltipChip } from '../../common/TooltipChip';
import useValidatePatternField, { ValidateActivationEvent, ValidationTypes } from '../useValidatePatternField';
import { removeLeadingWhitespaceForValue } from '../../../shared/utils/stringUtils';
import { ValidationState } from '../../../shared/enums/ValidationState';

const useStyles = makeStyles(theme => ({
  root: ({ hasErrors }) => ({
    '& fieldset': () => ({
      borderColor: `${hasErrors && theme.palette.error.main}`,
      border: `${hasErrors && `2px solid ${theme.palette.error.main}`}`,
    }),
    '& .MuiFormControl-root': {
      minWidth: '18rem',
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: theme.palette.mainText,
    },
  }),
  customError: {
    '& .MuiInputBase-formControl fieldset': {
      border: `2px solid ${theme.palette.error.main} !important`,
    },
  },
  validateWidget: {
    position: 'relative',
  },
  validateChip: {
    position: 'absolute',
    left: '16rem',
    bottom: '1.1rem',
  },
  textStyle: {
    color: theme.palette.failed,
  },
}));

const ValidatePatternField = (props) => {
  const { t } = useTranslation();
  const { name, uiSchema, id, invalidErrorText, textFieldProps, triggerEvent, disabled } = props;

  const IS_TRIGGER_EVENT_ALL = triggerEvent === ValidateActivationEvent.ALL;
  const {
    validation,
    value,
    setValue,
    startValidationProcess,
    startValidationProcessForOnChange,
  } = useValidatePatternField(props);

  const customClasses = useStyles(validation === ValidationState.INVALID);
  const classNames = clsx(value && validation === ValidationState.INVALID && customClasses.customError);

  const _onChange = async (v) => {
    const val = removeLeadingWhitespaceForValue(v);
    setValue(val);

    if (triggerEvent === ValidateActivationEvent.CHANGE || IS_TRIGGER_EVENT_ALL) {
      await startValidationProcessForOnChange(val);
    }
  };

  const _onBlur = async () => {
    if (triggerEvent === ValidateActivationEvent.BLUR || IS_TRIGGER_EVENT_ALL) {
      await startValidationProcess();
    }
  };

  const _onFocus = async () => {
    if (triggerEvent === ValidateActivationEvent.FOCUS || IS_TRIGGER_EVENT_ALL) {
      await startValidationProcess();
    }
  };

  return (
    <FormControl className={customClasses.root}>
      <div className={customClasses.validateWidget}>
        <TextWidget
          {...textFieldProps}
          id={`${name}-input-${id}`}
          className={classNames}
          error={validation === ValidationState.INVALID}
          value={value}
          disabled={disabled}
          rows={1}
          multiline={false}
          size="medium"
          uiSchema={uiSchema}
          onChange={_onChange}
          onBlur={_onBlur}
          onFocus={_onFocus}
          variant="outlined"
          options={{ emptyValue: '' }}
          registry={props.registry}
        />
        {validation !== ValidationState.IDLE && value && (
          <span className={customClasses.validateChip}>
            <TooltipChip validation={validation} />
          </span>
        )}
      </div>
      {validation === ValidationState.INVALID && value && (
        <AssistiveText
          dismissIcon={true}
          textStyle={customClasses.textStyle}
          data-testid={`${name}-input-helper-text-${id}`}
          severity="error"
        >
          {t(invalidErrorText)}
        </AssistiveText>
      )}
    </FormControl>
  );
};

ValidatePatternField.defaultProps = {
  name: 'validate',
  textFieldProps: {},
  externalValidation: {},
  validationOrder: [ValidationTypes.BUSINESS_RULES_VALIDATION],
  triggerEvent: ValidateActivationEvent.BLUR,
  formatValidationResult: {},
  invalidErrorText: 'error',
};

export default ValidatePatternField;
